
import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { getCurrentUserSubordinates } from '../../../lib/real-data-service'
import { 
  Award,
  User,
  Calendar,
  Target,
  TrendingUp,
  Star,
  MessageSquare,
  CheckCircle,
  Plus,
  Minus,
  Save,
  Send,
  FileText,
  Clock,
  Users,
  Building,
  Lightbulb,
  AlertCircle,
  Eye,
  Edit
} from 'lucide-react'

export default function NewPerformancePage() {
  const { user } = useAuth()
  usePageTitle('Nova Avaliação')
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [evaluationType, setEvaluationType] = useState<'annual' | 'quarterly' | 'probation' | 'promotion'>('quarterly')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [employees, setEmployees] = useState<any[]>([])

  // Load subordinates data on component mount
  useEffect(() => {
    const loadSubordinatesData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        if (!user?.employee?.email) {
          setError('User not authenticated or employee data not available')
          setIsLoading(false)
          return
        }

        // Load subordinates data asynchronously
        const subordinates = await getCurrentUserSubordinates(user.employee.email)
        const employeesData = subordinates.map(emp => ({
          id: emp.id,
          name: emp.name,
          department: emp.department,
          position: emp.role,
          manager: user?.name || '',
          hireDate: emp.admissionDate || '2023-01-01'
        }))

        setEmployees(employeesData)
      } catch (err) {
        console.error('Error loading subordinates data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load subordinates data')
      } finally {
        setIsLoading(false)
      }
    }

    if (user) {
      loadSubordinatesData()
    }
  }, [user])

  const [evaluation, setEvaluation] = useState({
    employeeId: '',
    evaluatorId: user?.id || '',
    period: {
      start: '',
      end: '',
      type: 'quarterly' as const
    },
    competencies: {
      technical: {
        rating: 0,
        comments: ''
      },
      behavioral: {
        rating: 0,
        comments: ''
      },
      leadership: {
        rating: 0,
        comments: ''
      },
      communication: {
        rating: 0,
        comments: ''
      },
      teamwork: {
        rating: 0,
        comments: ''
      },
      innovation: {
        rating: 0,
        comments: ''
      }
    },
    goals: {
      achieved: [] as { goal: string; status: 'completed' | 'partial' | 'not_started'; comments: string }[],
      new: [] as { goal: string; deadline: string; priority: 'high' | 'medium' | 'low'; description: string }[]
    },
    strengths: [] as string[],
    improvements: [] as string[],
    development: {
      training: [] as string[],
      responsibilities: [] as string[],
      timeline: ''
    },
    overallRating: 0,
    summary: '',
    nextReviewDate: ''
  })

  const competencyLabels = {
    technical: 'Competências Técnicas',
    behavioral: 'Competências Comportamentais',
    leadership: 'Liderança',
    communication: 'Comunicação',
    teamwork: 'Trabalho em Equipe',
    innovation: 'Inovação e Criatividade'
  }

  const ratingLabels = {
    1: 'Abaixo das Expectativas',
    2: 'Atende Parcialmente',
    3: 'Atende às Expectativas',
    4: 'Supera as Expectativas',
    5: 'Excepcional'
  }

  const getRatingColor = (rating: number) => {
    switch (rating) {
      case 1: return 'text-red-400'
      case 2: return 'text-orange-400'
      case 3: return 'text-yellow-400'
      case 4: return 'text-brand-primary'
      case 5: return 'text-green-400'
      default: return 'text-gray-400'
    }
  }

  const addGoal = (type: 'achieved' | 'new') => {
    if (type === 'achieved') {
      setEvaluation({
        ...evaluation,
        goals: {
          ...evaluation.goals,
          achieved: [
            ...evaluation.goals.achieved,
            { goal: '', status: 'not_started', comments: '' }
          ]
        }
      })
    } else {
      setEvaluation({
        ...evaluation,
        goals: {
          ...evaluation.goals,
          new: [
            ...evaluation.goals.new,
            { goal: '', deadline: '', priority: 'medium', description: '' }
          ]
        }
      })
    }
  }

  const updateGoal = (type: 'achieved' | 'new', index: number, field: string, value: any) => {
    if (type === 'achieved') {
      const updatedGoals = [...evaluation.goals.achieved]
      updatedGoals[index] = { ...updatedGoals[index], [field]: value }
      setEvaluation({
        ...evaluation,
        goals: { ...evaluation.goals, achieved: updatedGoals }
      })
    } else {
      const updatedGoals = [...evaluation.goals.new]
      updatedGoals[index] = { ...updatedGoals[index], [field]: value }
      setEvaluation({
        ...evaluation,
        goals: { ...evaluation.goals, new: updatedGoals }
      })
    }
  }

  const removeGoal = (type: 'achieved' | 'new', index: number) => {
    if (type === 'achieved') {
      const updatedGoals = evaluation.goals.achieved.filter((_, i) => i !== index)
      setEvaluation({
        ...evaluation,
        goals: { ...evaluation.goals, achieved: updatedGoals }
      })
    } else {
      const updatedGoals = evaluation.goals.new.filter((_, i) => i !== index)
      setEvaluation({
        ...evaluation,
        goals: { ...evaluation.goals, new: updatedGoals }
      })
    }
  }

  const addItem = (field: 'strengths' | 'improvements', item: string) => {
    if (item.trim()) {
      setEvaluation({
        ...evaluation,
        [field]: [...evaluation[field], item.trim()]
      })
    }
  }

  const removeItem = (field: 'strengths' | 'improvements', index: number) => {
    setEvaluation({
      ...evaluation,
      [field]: evaluation[field].filter((_, i) => i !== index)
    })
  }

  const handleSave = () => {
    // Mock save functionality
  }

  const handleSubmit = () => {
    // Mock submit functionality
  }

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {[
        { step: 1, label: 'Seleção', icon: User },
        { step: 2, label: 'Competências', icon: Star },
        { step: 3, label: 'Metas', icon: Target },
        { step: 4, label: 'Desenvolvimento', icon: TrendingUp },
        { step: 5, label: 'Resumo', icon: CheckCircle }
      ].map(({ step, label, icon: Icon }) => (
        <div key={step} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
            currentStep >= step 
              ? 'bg-brand-primary border-brand-primary text-brand-dark' 
              : 'border-gray-600 text-gray-400'
          }`}>
            {currentStep > step ? (
              <CheckCircle size={20} />
            ) : (
              <Icon size={20} />
            )}
          </div>
          <div className="ml-3">
            <p className={`text-sm font-medium ${
              currentStep >= step ? 'text-brand-primary' : 'text-gray-400'
            }`}>
              Etapa {step}
            </p>
            <p className={`text-xs ${
              currentStep >= step ? 'text-white' : 'text-gray-500'
            }`}>
              {label}
            </p>
          </div>
          {step < 5 && (
            <div className={`w-16 h-0.5 mx-4 ${
              currentStep > step ? 'bg-brand-primary' : 'bg-gray-600'
            }`} />
          )}
        </div>
      ))}
    </div>
  )

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in-up">
      <div className="card-hover">
        <h3 className="font-display text-lg font-semibold text-white mb-4">Informações da Avaliação</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">
              Funcionário a ser Avaliado
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => {
                setSelectedEmployee(e.target.value)
                setEvaluation({...evaluation, employeeId: e.target.value})
              }}
              className="form-select"
            >
              <option value="">Selecione um funcionário</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} - {employee.position}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">
              Tipo de Avaliação
            </label>
            <select
              value={evaluationType}
              onChange={(e) => {
                setEvaluationType(e.target.value as any)
                setEvaluation({
                  ...evaluation,
                  period: { ...evaluation.period, type: e.target.value as any }
                })
              }}
              className="form-select"
            >
              <option value="quarterly">Avaliação Trimestral</option>
              <option value="annual">Avaliação Anual</option>
              <option value="probation">Avaliação de Experiência</option>
              <option value="promotion">Avaliação para Promoção</option>
            </select>
          </div>

          <div>
            <label className="form-label">
              Período - Início
            </label>
            <input
              type="date"
              value={evaluation.period.start}
              onChange={(e) => setEvaluation({
                ...evaluation,
                period: { ...evaluation.period, start: e.target.value }
              })}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">
              Período - Fim
            </label>
            <input
              type="date"
              value={evaluation.period.end}
              onChange={(e) => setEvaluation({
                ...evaluation,
                period: { ...evaluation.period, end: e.target.value }
              })}
              className="form-input"
            />
          </div>
        </div>

        {selectedEmployee && (
          <div className="mt-6 p-4 bg-brand-dark rounded-lg">
            <h4 className="text-white font-medium mb-3">Dados do Funcionário</h4>
            {(() => {
              const employee = employees.find(e => e.id === Number(selectedEmployee))
              return employee ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-brand-gray text-sm">Nome</p>
                    <p className="text-white">{employee.name}</p>
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Cargo</p>
                    <p className="text-white">{employee.position}</p>
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Departamento</p>
                    <p className="text-white">{employee.department}</p>
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Gestor</p>
                    <p className="text-white">{employee.manager}</p>
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Data de Admissão</p>
                    <p className="text-white">{new Date(employee.employment.startDate).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Tempo na Empresa</p>
                    <p className="text-white">
                      {Math.floor((new Date().getTime() - new Date(employee.employment.startDate).getTime()) / (1000 * 60 * 60 * 24 * 365))} anos
                    </p>
                  </div>
                </div>
              ) : null
            })()}
          </div>
        )}
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium text-lg mb-4">Avaliação de Competências</h3>
        <p className="text-brand-gray mb-6">
          Avalie cada competência em uma escala de 1 a 5, onde 5 representa desempenho excepcional.
        </p>

        <div className="space-y-8">
          {Object.entries(competencyLabels).map(([key, label]) => (
            <div key={key} className="border-b border-gray-700 pb-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-medium">{label}</h4>
                <div className="flex items-center space-x-2">
                  <span className="text-brand-gray text-sm">Nota:</span>
                  <span className={`font-bold text-lg ${getRatingColor(evaluation.competencies[key as keyof typeof evaluation.competencies].rating)}`}>
                    {evaluation.competencies[key as keyof typeof evaluation.competencies].rating || '0'}
                  </span>
                </div>
              </div>

             <div className="grid grid-cols-5 gap-2 mb-4">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    onClick={() => setEvaluation({
                      ...evaluation,
                      competencies: {
                        ...evaluation.competencies,
                        [key]: {
                          ...evaluation.competencies[key as keyof typeof evaluation.competencies],
                          rating
                        }
                      }
                    })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      evaluation.competencies[key as keyof typeof evaluation.competencies].rating === rating
                        ? 'border-brand-primary bg-brand-primary bg-opacity-20 text-brand-primary'
                        : 'border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl font-bold">{rating}</div>
                      <div className="text-xs mt-1">{ratingLabels[rating as keyof typeof ratingLabels]}</div>
                    </div>
                  </button>
                ))}
              </div>

              <textarea
                value={evaluation.competencies[key as keyof typeof evaluation.competencies].comments}
                onChange={(e) => setEvaluation({
                  ...evaluation,
                  competencies: {
                    ...evaluation.competencies,
                    [key]: {
                      ...evaluation.competencies[key as keyof typeof evaluation.competencies],
                      comments: e.target.value
                    }
                  }
                })}
                placeholder={`Comentários sobre ${label.toLowerCase()}...`}
                className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none resize-none"
                rows={3}
              />
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-brand-dark rounded-lg">
          <h4 className="text-white font-medium mb-2">Avaliação Geral</h4>
          <div className="flex items-center space-x-4">
            <span className="text-brand-gray">Nota Final:</span>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  onClick={() => setEvaluation({...evaluation, overallRating: rating})}
                  className={`w-12 h-12 rounded-lg border-2 font-bold text-lg transition-all ${
                    evaluation.overallRating === rating
                      ? 'border-brand-primary bg-brand-primary text-brand-dark'
                      : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>
            <span className={`font-medium ${getRatingColor(evaluation.overallRating)}`}>
              {evaluation.overallRating ? ratingLabels[evaluation.overallRating as keyof typeof ratingLabels] : 'Não avaliado'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div className="space-y-6">
      {/* Metas Anteriores */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium text-lg">Metas do Período Anterior</h3>
          <button
            onClick={() => addGoal('achieved')}
            className="text-brand-primary hover:text-white flex items-center text-sm"
          >
            <Plus size={16} className="mr-1" />
            Adicionar Meta
          </button>
        </div>

        <div className="space-y-4">
          {evaluation.goals.achieved.map((goal, index) => (
            <div key={index} className="p-4 bg-brand-dark rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray mb-2">
                    Descrição da Meta
                  </label>
                  <input
                    type="text"
                    value={goal.goal}
                    onChange={(e) => updateGoal('achieved', index, 'goal', e.target.value)}
                    className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                    placeholder="Ex: Aumentar conversão em 15%"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-gray mb-2">
                    Status
                  </label>
                  <select
                    value={goal.status}
                    onChange={(e) => updateGoal('achieved', index, 'status', e.target.value)}
                    className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                  >
                    <option value="completed">Concluída</option>
                    <option value="partial">Parcialmente Concluída</option>
                    <option value="not_started">Não Iniciada</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  Comentários
                </label>
                <textarea
                  value={goal.comments}
                  onChange={(e) => updateGoal('achieved', index, 'comments', e.target.value)}
                  className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none resize-none"
                  rows={2}
                  placeholder="Comentários sobre o alcance da meta..."
                />
              </div>

              <button
                onClick={() => removeGoal('achieved', index)}
                className="text-red-400 hover:text-red-300 text-sm flex items-center"
              >
                <Minus size={16} className="mr-1" />
                Remover Meta
              </button>
            </div>
          ))}

          {evaluation.goals.achieved.length === 0 && (
            <div className="text-center py-8">
              <Target size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nenhuma meta anterior cadastrada</p>
            </div>
          )}
        </div>
      </div>

      {/* Novas Metas */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium text-lg">Metas para o Próximo Período</h3>
          <button
            onClick={() => addGoal('new')}
            className="text-brand-primary hover:text-white flex items-center text-sm"
          >
            <Plus size={16} className="mr-1" />
            Adicionar Meta
          </button>
        </div>

        <div className="space-y-4">
          {evaluation.goals.new.map((goal, index) => (
            <div key={index} className="p-4 bg-brand-dark rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray mb-2">
                    Meta
                  </label>
                  <input
                    type="text"
                    value={goal.goal}
                    onChange={(e) => updateGoal('new', index, 'goal', e.target.value)}
                    className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                    placeholder="Ex: Implementar nova metodologia de trabalho"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-gray mb-2">
                    Prazo
                  </label>
                  <input
                    type="date"
                    value={goal.deadline}
                    onChange={(e) => updateGoal('new', index, 'deadline', e.target.value)}
                    className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray mb-2">
                    Prioridade
                  </label>
                  <select
                    value={goal.priority}
                    onChange={(e) => updateGoal('new', index, 'priority', e.target.value)}
                    className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                  >
                    <option value="high">Alta</option>
                    <option value="medium">Média</option>
                    <option value="low">Baixa</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  Descrição Detalhada
                </label>
                <textarea
                  value={goal.description}
                  onChange={(e) => updateGoal('new', index, 'description', e.target.value)}
                  className="w-full px-4 py-2 bg-brand-dark-light border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none resize-none"
                  rows={3}
                  placeholder="Descrição detalhada da meta e como será mensurada..."
                />
              </div>

              <button
                onClick={() => removeGoal('new', index)}
                className="text-red-400 hover:text-red-300 text-sm flex items-center"
              >
                <Minus size={16} className="mr-1" />
                Remover Meta
              </button>
            </div>
          ))}

          {evaluation.goals.new.length === 0 && (
            <div className="text-center py-8">
              <Target size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nenhuma nova meta definida</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderStep4 = () => {
    const [newStrength, setNewStrength] = useState('')
    const [newImprovement, setNewImprovement] = useState('')
    const [newTraining, setNewTraining] = useState('')
    const [newResponsibility, setNewResponsibility] = useState('')

    return (
      <div className="space-y-6">
        {/* Pontos Fortes */}
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-white font-medium text-lg mb-4">Pontos Fortes</h3>
          
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="text"
              value={newStrength}
              onChange={(e) => setNewStrength(e.target.value)}
              className="flex-1 px-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
              placeholder="Ex: Excelente capacidade de comunicação"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addItem('strengths', newStrength)
                  setNewStrength('')
                }
              }}
            />
            <button
              onClick={() => {
                addItem('strengths', newStrength)
                setNewStrength('')
              }}
              className="bg-brand-primary text-brand-dark px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>

          <div className="space-y-2">
            {evaluation.strengths.map((strength, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-brand-dark rounded-lg">
                <div className="flex items-center">
                  <CheckCircle size={16} className="text-green-400 mr-2" />
                  <span className="text-white">{strength}</span>
                </div>
                <button
                  onClick={() => removeItem('strengths', index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Minus size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Pontos de Melhoria */}
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-white font-medium text-lg mb-4">Pontos de Melhoria</h3>
          
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="text"
              value={newImprovement}
              onChange={(e) => setNewImprovement(e.target.value)}
              className="flex-1 px-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
              placeholder="Ex: Melhorar gestão do tempo"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addItem('improvements', newImprovement)
                  setNewImprovement('')
                }
              }}
            />
            <button
              onClick={() => {
                addItem('improvements', newImprovement)
                setNewImprovement('')
              }}
              className="bg-brand-primary text-brand-dark px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>

          <div className="space-y-2">
            {evaluation.improvements.map((improvement, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-brand-dark rounded-lg">
                <div className="flex items-center">
                  <AlertCircle size={16} className="text-yellow-400 mr-2" />
                  <span className="text-white">{improvement}</span>
                </div>
                <button
                  onClick={() => removeItem('improvements', index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Minus size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Plano de Desenvolvimento */}
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-white font-medium text-lg mb-4">Plano de Desenvolvimento</h3>
          
          {/* Treinamentos */}
          <div className="mb-6">
            <h4 className="text-white font-medium mb-3">Treinamentos Recomendados</h4>
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="text"
                value={newTraining}
                onChange={(e) => setNewTraining(e.target.value)}
                className="flex-1 px-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                placeholder="Ex: Curso de Liderança Executiva"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setEvaluation({
                      ...evaluation,
                      development: {
                        ...evaluation.development,
                        training: [...evaluation.development.training, newTraining]
                      }
                    })
                    setNewTraining('')
                  }
                }}
              />
              <button
                onClick={() => {
                  setEvaluation({
                    ...evaluation,
                    development: {
                      ...evaluation.development,
                      training: [...evaluation.development.training, newTraining]
                    }
                  })
                  setNewTraining('')
                }}
                className="bg-brand-primary text-brand-dark px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            
            <div className="space-y-2">
              {evaluation.development.training.map((training, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-brand-dark rounded-lg">
                  <div className="flex items-center">
                    <Lightbulb size={16} className="text-brand-primary mr-2" />
                    <span className="text-white">{training}</span>
                  </div>
                  <button
                    onClick={() => {
                      const updatedTraining = evaluation.development.training.filter((_, i) => i !== index)
                      setEvaluation({
                        ...evaluation,
                        development: { ...evaluation.development, training: updatedTraining }
                      })
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Minus size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Novas Responsabilidades */}
          <div className="mb-6">
            <h4 className="text-white font-medium mb-3">Novas Responsabilidades</h4>
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="text"
                value={newResponsibility}
                onChange={(e) => setNewResponsibility(e.target.value)}
                className="flex-1 px-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
                placeholder="Ex: Mentoria de novos funcionários"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setEvaluation({
                      ...evaluation,
                      development: {
                        ...evaluation.development,
                        responsibilities: [...evaluation.development.responsibilities, newResponsibility]
                      }
                    })
                    setNewResponsibility('')
                  }
                }}
              />
              <button
                onClick={() => {
                  setEvaluation({
                    ...evaluation,
                    development: {
                      ...evaluation.development,
                      responsibilities: [...evaluation.development.responsibilities, newResponsibility]
                    }
                  })
                  setNewResponsibility('')
                }}
                className="bg-brand-primary text-brand-dark px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            
            <div className="space-y-2">
              {evaluation.development.responsibilities.map((responsibility, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-brand-dark rounded-lg">
                  <div className="flex items-center">
                    <Building size={16} className="text-blue-400 mr-2" />
                    <span className="text-white">{responsibility}</span>
                  </div>
                  <button
                    onClick={() => {
                      const updatedResponsibilities = evaluation.development.responsibilities.filter((_, i) => i !== index)
                      setEvaluation({
                        ...evaluation,
                        development: { ...evaluation.development, responsibilities: updatedResponsibilities }
                      })
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Minus size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Cronograma de Desenvolvimento
            </label>
            <textarea
              value={evaluation.development.timeline}
              onChange={(e) => setEvaluation({
                ...evaluation,
                development: { ...evaluation.development, timeline: e.target.value }
              })}
              className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none resize-none"
              rows={4}
              placeholder="Descreva o cronograma e marcos importantes do plano de desenvolvimento..."
            />
          </div>
        </div>
      </div>
    )
  }

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium text-lg mb-4">Resumo da Avaliação</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-brand-gray mb-2">
            Comentários Gerais
          </label>
          <textarea
            value={evaluation.summary}
            onChange={(e) => setEvaluation({...evaluation, summary: e.target.value})}
            className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none resize-none"
            rows={5}
            placeholder="Resumo geral da avaliação, destacando principais conquistas e áreas de foco..."
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-brand-gray mb-2">
            Data da Próxima Avaliação
          </label>
          <input
            type="date"
            value={evaluation.nextReviewDate}
            onChange={(e) => setEvaluation({...evaluation, nextReviewDate: e.target.value})}
            className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none"
          />
        </div>

        {/* Preview */}
        <div className="bg-brand-dark p-6 rounded-lg">
          <h4 className="text-white font-medium mb-4">Prévia da Avaliação</h4>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-brand-gray">Funcionário:</span>
              <span className="text-white">
                {employees.find(e => e.id === Number(selectedEmployee))?.name || 'Não selecionado'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-brand-gray">Tipo de Avaliação:</span>
              <span className="text-white">
                {evaluationType === 'quarterly' ? 'Trimestral' :
                 evaluationType === 'annual' ? 'Anual' :
                 evaluationType === 'probation' ? 'Experiência' : 'Promoção'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-brand-gray">Nota Geral:</span>
              <span className={`font-bold text-lg ${getRatingColor(evaluation.overallRating)}`}>
                {evaluation.overallRating || 'N/A'} 
                {evaluation.overallRating ? ` - ${ratingLabels[evaluation.overallRating as keyof typeof ratingLabels]}` : ''}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-brand-gray">Metas Anteriores:</span>
              <span className="text-white">{evaluation.goals.achieved.length}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-brand-gray">Novas Metas:</span>
              <span className="text-white">{evaluation.goals.new.length}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-brand-gray">Pontos Fortes:</span>
              <span className="text-white">{evaluation.strengths.length}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-brand-gray">Melhorias:</span>
              <span className="text-white">{evaluation.improvements.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      
                <div className="space-y-8">

                  {/* Loading State */}
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <h3 className="font-display text-lg font-medium text-white mb-2">
                      Carregando dados da equipe...
                    </h3>
                    <p className="text-brand-gray max-w-md mx-auto">
                      Buscando informações dos subordinados para avaliação
                    </p>
                  </div>
                </div>
              
    )
  }

  if (error) {
    return (
      
                <div className="space-y-8">

                  {/* Error State */}
                  <div className="text-center py-12">
                    <div className="text-brand-red mb-4">
                      <AlertCircle className="mx-auto h-12 w-12" />
                    </div>
                    <h3 className="font-display text-lg font-medium text-white mb-2">
                      Erro ao carregar dados
                    </h3>
                    <p className="text-brand-gray max-w-md mx-auto mb-4">
                      {error}
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="btn-primary"
                    >
                      Tentar Novamente
                    </button>
                  </div>
                </div>
              
    )
  }

  return (
    
            <div className="space-y-8">
              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleSave}
                  className="btn-ghost flex items-center gap-2 group"
                >
                  <Save size={16} className="transition-transform duration-200 group-hover:scale-110" />
                  Salvar Rascunho
                </button>

                {currentStep === 5 && (
                  <button
                    onClick={handleSubmit}
                    className="btn-primary flex items-center gap-2 group"
                  >
                    <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                    Finalizar Avaliação
                  </button>
                )}
              </div>

              {/* Step Indicator */}
              {renderStepIndicator()}

              {/* Step Content */}
              <div>
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
                {currentStep === 4 && renderStep4()}
                {currentStep === 5 && renderStep5()}
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                  className="btn-ghost disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                
                <button
                  onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
                  disabled={currentStep === 5 || (currentStep === 1 && !selectedEmployee)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentStep === 5 ? 'Concluído' : 'Próximo'}
                </button>
              </div>
            </div>
          
  )
} 